import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Input, List, Select, Tag } from "antd";
import { GoogleMap, Polygon, Marker } from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "420px",
};

const LabMapWithPolygon = ({
  isOpen,
  onClose,
  onSave,
  islat,
  isLong,
  departments = [],
  defaultDepartment,
  mode = "add", // "add" | "edit"
}) => {
  const [path, setPath] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentCenter, setCurrentCenter] = useState({
    lat: islat || 19.0859,
    lng: isLong || 72.9076,
  });

  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedDepartment, setSelectedDepartment] =
    useState(defaultDepartment);

  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    if (!autocompleteServiceRef.current && window.google) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
    }

    if (isOpen) {
      setSelectedDepartment(defaultDepartment);
      setPath([]);
      setSearchText("");
      setIsDrawing(false);
    }
  }, [isOpen, defaultDepartment]);

  const getDepartmentName = () => {
    const dept = departments.find((d) => d.id === selectedDepartment);
    return dept ? dept.name.toUpperCase() : "";
  };

  const handleMapClick = (e) => {
    if (!isDrawing) return;

    const newPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setPath((prev) => [...prev, newPoint]);
    setCurrentCenter(newPoint);
  };

  const clearPolygon = () => {
    setPath([]);
    setCurrentCenter({
      lat: islat || 19.0859,
      lng: isLong || 72.9076,
    });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (!value) {
      setSuggestions([]);
      return;
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (autocompleteServiceRef.current) {
        autocompleteServiceRef.current.getPlacePredictions(
          { input: value },
          (predictions, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              setSuggestions(predictions);
            } else {
              setSuggestions([]);
            }
          }
        );
      }
    }, 800);
  };

  const handleSuggestionClick = (placeId) => {
    if (!placesServiceRef.current) {
      placesServiceRef.current =
        new window.google.maps.places.PlacesService(
          document.createElement("div")
        );
    }

    placesServiceRef.current.getDetails({ placeId }, (place, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        place.geometry
      ) {
        const newPoint = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        setPath((prev) => [...prev, newPoint]);
        setCurrentCenter(newPoint);
        setSearchText(place.formatted_address || "");
        setSuggestions([]);
      }
    });
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontWeight: 600 }}>
            {mode === "edit" ? "Edit Territory" : "Create Territory"}
          </span>
          {selectedDepartment && (
            <Tag color="blue">{getDepartmentName()}</Tag>
          )}
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={() => {
            onSave(path, selectedDepartment);
            clearPolygon();
            setSearchText("");
          }}
          disabled={!selectedDepartment || path.length === 0}
        >
          Save Coordinates
        </Button>,
      ]}
    >
      {/* Search + Department Row */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "16px",
          alignItems: "flex-start",
        }}
      >
        <div style={{ flex: 1, position: "relative" }}>
          <Input
            placeholder="Search location..."
            value={searchText}
            onChange={handleSearchChange}
          />

          {suggestions.length > 0 && (
            <List
              bordered
              style={{
                position: "absolute",
                top: "42px",
                width: "100%",
                zIndex: 1000,
                background: "#fff",
                maxHeight: "200px",
                overflowY: "auto",
              }}
              dataSource={suggestions}
              renderItem={(item) => (
                <List.Item
                  key={item.place_id}
                  onClick={() => handleSuggestionClick(item.place_id)}
                  style={{ cursor: "pointer" }}
                >
                  {item.description}
                </List.Item>
              )}
            />
          )}
        </div>

        <Select
          style={{ width: 240 }}
          placeholder="Select Department"
          value={selectedDepartment}
          onChange={(value) => setSelectedDepartment(value)}
          disabled={mode === "edit"}
        >
          {departments.map((dept) => (
            <Select.Option key={dept.id} value={dept.id}>
              {dept.name.toUpperCase()}
            </Select.Option>
          ))}
        </Select>
      </div>

      {/* Drawing Controls */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <Button
          type={isDrawing ? "default" : "primary"}
          onClick={() => setIsDrawing((prev) => !prev)}
        >
          {isDrawing ? "Stop Drawing" : "Start Drawing"}
        </Button>
        <Button onClick={clearPolygon}>Clear</Button>
      </div>

      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={currentCenter}
        zoom={10}
        onClick={handleMapClick}
      >
        <Polygon
          path={path}
          options={{
            fillColor: "rgba(63, 81, 181, 0.4)",
            strokeColor: "#3f51b5",
            strokeWeight: 2,
          }}
        />

        {path.map((point, index) => (
          <Marker key={index} position={point} />
        ))}
      </GoogleMap>

      {/* Coordinates Preview */}
      <div style={{ marginTop: "20px" }}>
        <h4 style={{ fontWeight: 600 }}>Selected Coordinates:</h4>
        {path.length > 0 ? (
          <ul>
            {path.map((point, index) => (
              <li key={index}>
                Latitude: {point.lat}, Longitude: {point.lng}
              </li>
            ))}
          </ul>
        ) : (
          <p>No coordinates selected.</p>
        )}
      </div>
    </Modal>
  );
};

export default LabMapWithPolygon;
